import React from 'react';
import { Box, Card, Typography, Button, Avatar, Chip, Stack, IconButton } from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import FavoriteIcon from '@mui/icons-material/Favorite';
import StarIcon from '@mui/icons-material/Star';
import PeopleIcon from '@mui/icons-material/People';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { toggleFavorite } from '../../../actions/users';
import { urlImg, isEmpty } from '../../common/helpers';
import { useIrAEntrar } from '../../../utils/destinoAlEntrar';

const ProfilePageCard = ({ page, user: ownerUser, profile, isGroup = false }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const authUser = useSelector(state => state.user.user);
  const allCategories = useSelector(state => state.cat.categories);
  const isFavorite = authUser?.favorites?.some(f => f.toString() === page._id?.toString());
  // Sin sesión el corazón manda a entrar (antes la petición fallaba sin decir nada); al entrar regresa a este perfil
  const irAEntrar = useIrAEntrar();

  const {
    _id,
    nombre,
    descripcion,
    img,
    categoria,
    categoriaNombre,
    ubicacion,
    servicios = [],
    miembros = []
  } = page;

  const categoryName = React.useMemo(() => {
    if (!categoria) return 'sin categoría';
    // nombre resuelto por el backend (funciona aunque la categoría esté deshabilitada)
    if (categoriaNombre) return categoriaNombre;
    const found = allCategories.find(c => c._id === categoria || c.name === categoria);
    // nunca mostrar el _id crudo
    return found ? found.name : 'sin categoría';
  }, [categoria, categoriaNombre, allCategories]);

  // Calificación de la página = promedio ponderado de sus servicios (por nº de reseñas), y cuántas opiniones suman
  const { ratingPagina, opinionesPagina } = React.useMemo(() => {
    const ids = (servicios || []).map(ps => (ps.servicio?._id || ps.servicio || ps)?.toString());
    const calificados = (profile?.servicios || [])
      .filter(s => ids.includes(s._id?.toString()) && s.ratingPromedio > 0 && s.ratingTotal > 0);
    const totalResenas = calificados.reduce((acc, s) => acc + s.ratingTotal, 0);
    return {
      opinionesPagina: totalResenas,
      ratingPagina: totalResenas > 0
        ? calificados.reduce((acc, s) => acc + s.ratingPromedio * s.ratingTotal, 0) / totalResenas
        : 0,
    };
  }, [servicios, profile]);

  // If page doesn't have an image, use a placeholder or the profile user's banner if applicable
  const pageImg = !isEmpty(img)
    ? `${urlImg}/paginas/${_id}/${img}`
    : 'https://images.unsplash.com/photo-1555507036-ab1f4038808a?q=80&w=1926&auto=format&fit=crop';

  const userAvatar = !isEmpty(ownerUser?.avatar)
    ? `${urlImg}/w_48,h_48,c_fill/users/${ownerUser._id}/${ownerUser.avatar}`
    : '/search.png';

  const handleViewPage = () => {
    navigate(`/pagina/${_id}`);
  };

  const acceptedMembersCount = miembros.filter(m => m.status === 1).length;

  return (
    <Card
      elevation={0}
      sx={{
        display: 'flex',
        borderRadius: '16px',
        border: 1, borderColor: 'sutil',
        overflow: 'hidden',
        mb: 2,
        transition: 'transform 0.2s',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: '0 4px 20px rgba(0,0,0,0.05)'
        }
      }}
    >
      {/* Left Image Section */}
      <Box sx={{ position: 'relative', width: { xs: 150, sm: 220 }, height: 200 }}>
        <Box
          component="img"
          src={pageImg}
          alt={nombre}
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'cover'
          }}
        />

        {/* Category Chip */}
        <Chip
          label={categoryName}
          size="small"
          sx={{
            position: 'absolute',
            top: 12,
            left: 12,
            bgcolor: 'primary.main',
            color: 'white',
            fontWeight: 'bold',
            borderRadius: '8px',
            fontSize: '11px',
            '& .MuiChip-label': { px: 1 }
          }}
        />

        {/* Favorite Icon */}
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            if (!authUser?._id) return irAEntrar();
            dispatch(toggleFavorite(_id));
          }}
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            color: isFavorite ? 'primary.main' : 'white',
            bgcolor: isFavorite ? 'rgba(255,255,255,0.9)' : 'rgba(0,0,0,0.2)',
            '&:hover': { bgcolor: isFavorite ? 'white' : 'rgba(0,0,0,0.4)' }
          }}
        >
          {isFavorite ? <FavoriteIcon /> : <FavoriteBorderIcon />}
        </IconButton>

        {/* Provider Overlay */}
        <Box
          sx={{
            position: 'absolute',
            bottom: 12,
            left: 12,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            color: 'white'
          }}
        >
          <Avatar
            src={userAvatar}
            sx={{ width: 24, height: 24, border: '1px solid rgba(255,255,255,0.5)' }}
          />
          <Typography variant="caption" fontWeight="bold" sx={{ textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}>
            {ownerUser?.name || profile?.nombre || 'Proveedor'}
          </Typography>
        </Box>
      </Box>

      {/* Right Content Section */}
      <Box sx={{ flex: 1, p: { xs: 2, sm: 3 }, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box sx={{ flex: 1 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="h6" fontWeight="bold" color="primary" sx={{ fontSize: { xs: '0.95rem', sm: '1.15rem' }, lineHeight: 1.2 }}>
                {nombre}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {ratingPagina > 0 ? (
                  <>
                    <StarIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                    <Typography variant="caption" fontWeight="bold" color="primary">{ratingPagina.toFixed(1)}</Typography>
                  </>
                ) : (
                  <Typography variant="caption" fontWeight="bold" color="text.secondary">Nuevo</Typography>
                )}
              </Box>
            </Stack>

            <Typography 
              variant="body2" 
              color="text.secondary" 
              sx={{ 
                mb: 2, 
                display: '-webkit-box', 
                WebkitLineClamp: 2, 
                WebkitBoxOrient: 'vertical', 
                overflow: 'hidden',
                lineHeight: 1.4
              }}
            >
              {descripcion}
            </Typography>

            <Stack direction="row" spacing={2} sx={{ mb: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <LocationOnIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                <Typography variant="caption" color="text.secondary">
                  {(() => {
                    if (!ubicacion) return 'Remoto';
                    const addr = profile?.direcciones?.find(d => d._id?.toString() === ubicacion?.toString());
                    return addr ? addr.direccion?.split(',')[0] : 'Ubicación';
                  })()}
                </Typography>
              </Box>
              {isGroup && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <PeopleIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                  <Typography variant="caption" color="text.secondary">
                    {miembros.length} participantes
                  </Typography>
                </Box>
              )}
            </Stack>

            {/* Cuántas opiniones suman sus servicios (como la ficha del servicio); sin opiniones no se dice nada */}
            <Typography variant="caption" color="text.secondary">
              {opinionesPagina > 0 && `${opinionesPagina} ${opinionesPagina === 1 ? 'opinión' : 'opiniones'}`}
            </Typography>
          </Box>

          <Box sx={{ textAlign: 'right', ml: 2, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', justifyContent: 'center', height: '100%', minWidth: '120px' }}>
            <Typography variant="h5" fontWeight="bold" color="primary" sx={{ mb: 0 }}>
              {servicios.length}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 3 }}>
              Servicios disponibles
            </Typography>

            <Button
              variant="contained"
              onClick={handleViewPage}
              sx={{
                textTransform: 'none',
                fontWeight: 'bold',
                borderRadius: '8px',
                px: { xs: 1.5, sm: 3 },
                fontSize: { xs: '12px', sm: '14px' }
              }}
            >
              Ver página
            </Button>
          </Box>
        </Box>
      </Box>
    </Card>
  );
};

export default ProfilePageCard;
